// @ts-ignore - unfortunately no typing for this package
import namehash from 'eth-ens-namehash';
import type { BaseContract } from 'ethers';

import { ErcChainId } from '../../../types/web3/web3';
import { captureBreadcrumb, captureError } from '../../../utils/capture';
import { isMainnet } from '../../../utils/web3/chains';
import { useContract } from './useContract';

/**
 * this hook provide simple api to communicate with ENS
 * https://docs.ens.domains/dapp-developer-guide/resolving-names#reverse-resolution
 *
 * In the future we should use ENSjsV2 (V1 is deprecated) in order to get reverse resolution,
 * but for the meanwhile the way to achieve it is either through ethers for a single address or by calling the reverse records contract manually.
 * https://github.com/ensdomains/ensjs#note-we-are-working-on-the-next-version-of-this-library-which-will-include-batch-calling-and-other-features-once-that-is-out-we-will-be-deprecating-this-library-we-generally-recommend-ethersjs
 */

// a partial ABI for the ENS ReverseRecords contract
const abi = [
  {
    'inputs': [
      { 'internalType': 'address[]', 'name': 'addresses', 'type': 'address[]' },
    ],
    'name': 'getNames',
    'outputs': [
      { 'internalType': 'string[]', 'name': 'r', 'type': 'string[]' },
    ],
    'stateMutability': 'view',
    'type': 'function',
  },
];

/**
 * ENS does not support every chain, so for polygon/binance we would still use the ethereum mainnet ENS lookup
 * for testnets we only respect goerli.
 * see list here
 * https://github.com/ensdomains/reverse-records
 */
const ENS_REVERSE_RECORDS_MAINNET =
  '0x3671aE578E63FdF66ad4F3E12CC0c0d71Ac7510C';
const ENS_REVERSE_RECORDS_GOERLI = '0x333Fc8f550043f239a2CF79aEd5e9cF4A20Eb41e';

interface ReverseRecords extends BaseContract {
  getNames: (addresses: string[]) => Promise<string[]>;
}

const useReverseRecordsContract = (chain: ErcChainId) => {
  const address = isMainnet(chain)
    ? ENS_REVERSE_RECORDS_MAINNET
    : ENS_REVERSE_RECORDS_GOERLI;
  const chainId = isMainnet(chain) ? '0x1' : '0x5';

  return useContract<ReverseRecords>({
    abi,
    address,
    chainId,
  });
};

// caches the ens addresses
const cache: Map<string, string> = new Map();
const DEFAULT_ENS_VALUE = '';

const cacheName = (address: string, name?: string | null) => {
  // prevents homograph attacks
  if (name && namehash.normalize(name) === name) {
    cache.set(address, name);
    return name;
  }
  cache.set(address, DEFAULT_ENS_VALUE);
  return DEFAULT_ENS_VALUE;
};

export const useEns = (chainId: ErcChainId) => {
  const { contract } = useReverseRecordsContract(chainId)!;

  // translate multiple addresses to ENS names
  const bulkReverseResolution = async (
    addresses: string[],
  ): Promise<string[]> => {
    const normalizedAddresses = addresses.map((address) =>
      address.toLowerCase(),
    );
    // check what names are cached
    const uncachedAddresses = normalizedAddresses.filter(
      (address) => !cache.has(address),
    );
    // fetch uncached names and cache them
    if (uncachedAddresses.length > 0) {
      try {
        const names = await contract.getNames(uncachedAddresses);
        uncachedAddresses.forEach((address, index) =>
          cacheName(address, names[index]),
        );
      } catch (err) {
        captureBreadcrumb({
          level: 'error',
          category: 'ens',
          message: 'ens error',
        });
        captureError(err);
      }
    }
    // return the addresses from cache
    const map = normalizedAddresses.map(
      (address) => cache.get(address) || DEFAULT_ENS_VALUE,
    );
    return map;
  };

  // translate a single address to ENS name
  const reverseResolution = async (address: string): Promise<string> => {
    const normalizedAddress = address.toLowerCase();
    if (cache.has(normalizedAddress)) {
      return cache.get(normalizedAddress) || DEFAULT_ENS_VALUE;
    }
    try {
      const [name] = await contract.getNames([normalizedAddress]);
      return cacheName(normalizedAddress, name);
    } catch (err) {
      return DEFAULT_ENS_VALUE;
    }
  };

  return {
    bulkReverseResolution,
    reverseResolution,
  };
};
